import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  CheckInWidget,
  CheckInWidgetFormType
} from '../models/check-in-widget.interfaces';
import {
  CHECK_IN_TRIP_BY,
  FORM_FIELD_NAMES
} from '../models/constants/check-in-constants';
import { CheckInFormValidatorService } from './check-in-form-validator.service';
import { CheckInWidgetInitializationService } from './check-in-widget-initialization.service';

@Injectable({
  providedIn: 'root'
})
export class CheckInFormSubscriptionService {
  constructor(
    private validatorService: CheckInFormValidatorService,
    private initService: CheckInWidgetInitializationService
  ) {}

  private fieldValueCache: { [key: string]: string } = {};
  private lastTripByValue: string | null = null;

  setupFormSubscriptions(
    form: FormGroup<CheckInWidgetFormType>,
    content: CheckInWidget,
    onStateReset: () => void,
    onValidationError: () => void,
    isSubmitted: () => boolean
  ): Subscription {
    const subscription = new Subscription();

    subscription.add(
      form.get('findTripBy')?.valueChanges.subscribe((value) => {
        if (value) {
          this.handleTripByChange(form, value, content, onStateReset);
        }
      })
    );

    subscription.add(
      form.get('findTripByValue')?.valueChanges.subscribe((value) => {
        const tripBy = form.get('findTripBy')?.value;
        if (value && this.shouldFilterNumericOnly(tripBy)) {
          const filtered = value.replace(/\D/g, '');
          if (filtered !== value) {
            form
              .get('findTripByValue')
              ?.setValue(filtered, { emitEvent: false });
          }
        }
      })
    );

    [
      FORM_FIELD_NAMES.FIND_TRIP_BY_VALUE,
      FORM_FIELD_NAMES.LAST_NAME,
      FORM_FIELD_NAMES.ORIGIN_AIRPORT_CODE
    ].forEach((field) => {
      subscription.add(
        form.get(field)?.valueChanges.subscribe(() => {
          if (isSubmitted()) {
            onValidationError();
          }
        })
      );
    });

    return subscription;
  }

  private handleTripByChange(
    form: FormGroup<CheckInWidgetFormType>,
    value: string,
    content: CheckInWidget,
    onStateReset: () => void
  ): void {
    if (this.lastTripByValue) {
      const currentValue = form.get('findTripByValue')?.value;
      if (currentValue) {
        this.fieldValueCache[this.lastTripByValue] = currentValue;
      }
    }
    onStateReset();
    const cachedValue = this.fieldValueCache[value] || '';
    form.get('findTripByValue')?.setValue(cachedValue, { emitEvent: false });
    this.lastTripByValue = value;

    const validators = this.initService.getValidatorsForTripType(value);
    this.validatorService.updateValidators(form, 'findTripByValue', validators);

    const lastNameValidators = this.validatorService.getLastNameValidators(
      content,
      this.initService.hideLastName
    );
    this.validatorService.updateValidators(
      form,
      'lastName',
      lastNameValidators
    );
  }

  private shouldFilterNumericOnly(tripBy: string | null | undefined): boolean {
    return (
      tripBy === CHECK_IN_TRIP_BY.CREDIT_CARD_NUMBER ||
      tripBy === CHECK_IN_TRIP_BY.TICKET_NUMBER ||
      tripBy === CHECK_IN_TRIP_BY.SKYMILES_NUMBER
    );
  }
}
